// /car/map — Full-screen reverse-geocoding map picker (iter82).
//
// Behaviour:
//   - Edge-to-edge Mapbox dark map.
//   - Top gradient overlay: back button + title.
//   - Center crosshair stays fixed; the map pans beneath it.
//   - While the user is dragging: crosshair pulses, bottom panel shows
//     "Drag the map to move the pin" and CTA is disabled.
//   - When the map settles: crosshair turns solid gold, we hit Mapbox
//     reverse-geocoding for a friendly name; falls back to coords if offline
//     or the request fails.
//   - Tapping "Get a Ride Here" fires sendRideRequest() directly (WhatsApp
//     opens immediately, no popup) with the pinned destination and pushes
//     it into habano.ride.history.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  ArrowLeft, Car as CarIcon, LocateFixed, MapPin, Plane,
  Search, X as XIcon, Bed, Heart, Gem, Globe,
} from "lucide-react";
import { toast } from "sonner";
import { BottomNav } from "../components/BottomNav";
import { sendRideRequest } from "../lib/sendRideRequest";
import { readPendingRide, clearPendingRide, writeLastPickup } from "../lib/ridePickup";
import { useI18n } from "../lib/i18n";
import { useGps } from "../lib/useGps";
import { api, getMe } from "../lib/api";
import { readHotel } from "../lib/hotelStore";
import * as guestStore from "../lib/guestStorage";
import { pushHistory } from "./Car";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const HAVANA = { lng: -82.3666, lat: 23.1136 };
const AIRPORT = {
  name: "José Martí International Airport",
  address: "Terminal 2, Havana, Cuba",
  lat: 22.9892,
  lng: -82.4091,
};

/* Format coordinates as a fallback name when reverse-geocoding fails. */
function coordLabel(lng, lat) {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

/* Mapbox forward-geocoding restricted to the Havana viewport — used by the
   in-map search bar. Returns up to 4 results. */
async function forwardGeocodeHavana(query) {
  if (!TOKEN || !query) return [];
  if (typeof navigator !== "undefined" && navigator.onLine === false) return [];
  try {
    // bbox roughly covering the city of Havana + immediate suburbs.
    const bbox = "-82.55,23.00,-82.20,23.20";
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${TOKEN}&autocomplete=true&limit=4&language=en,es&bbox=${bbox}&proximity=-82.3666,23.1136`;
    const r = await fetch(url);
    if (!r.ok) return [];
    const data = await r.json();
    return (data.features || []).map((f) => ({
      kind: "geocode",
      name: f.text || f.place_name.split(",")[0],
      address: f.place_name || "",
      lat: f.center?.[1],
      lng: f.center?.[0],
    })).filter((r) => typeof r.lat === "number" && typeof r.lng === "number");
  } catch {
    return [];
  }
}

/* Mapbox reverse-geocoding — returns { name, address } or null on failure. */
async function reverseGeocode(lng, lat) {
  if (!TOKEN) return null;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return null;
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${TOKEN}&limit=1&language=en,es`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const data = await r.json();
    const f = (data.features || [])[0];
    if (!f) return null;
    // Prefer the most specific text and use place_name (full address) as the secondary line.
    const name = f.text || f.place_name?.split(",")[0] || coordLabel(lng, lat);
    const address = f.place_name && f.place_name !== name ? f.place_name : "";
    return { name, address };
  } catch {
    return null;
  }
}

export default function CarMap() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromAirport = params.get("pickup") === "airport";
  const pickupMode = params.get("mode") === "pickup";  // iter153
  const initialLat = parseFloat(params.get("lat"));
  const initialLng = parseFloat(params.get("lng"));
  const hasInitialCenter = Number.isFinite(initialLat) && Number.isFinite(initialLng);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const settleTimerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const { coords, requestGps } = useGps();
  const [moving, setMoving] = useState(false);
  const [pin, setPin] = useState(null);           // {name, address, lat, lng}
  const [geocoding, setGeocoding] = useState(false);
  // iter212 · Phase E — Live driver dots overlay.
  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const driverMarkersRef = useRef([]);

  // Search state.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocus, setSearchFocus] = useState(false);
  const [hotel, setHotel] = useState(null);
  const [savedBiz, setSavedBiz] = useState([]);
  const [customLocs, setCustomLocs] = useState([]);
  const [geocodeResults, setGeocodeResults] = useState([]);

  // Hydrate hotel + saved businesses + custom locations (one shot).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      const localHotel = readHotel();
      if (localHotel) setHotel(localHotel);
      if (!me) {
        if (!localHotel) setHotel(null);
        const saved = guestStore.getSaved().filter((s) => s.target_type === "business");
        const bizRows = await Promise.all(saved.slice(0, 20).map((s) =>
          api.get(`/businesses/${s.target_id}`).then((r) => r.data).catch(() => null),
        ));
        if (cancelled) return;
        setSavedBiz(bizRows.filter(Boolean));
        setCustomLocs(guestStore.getCustomLocations());
        return;
      }
      // Logged-in: saved-locations holds hotel (fallback when no local), favorites + custom-locations cover the rest.
      api.get("/me/saved-locations").then(({ data }) => {
        if (cancelled) return;
        if (readHotel()) return; // local store wins
        const list = data || [];
        setHotel(list.find((s) => s.category === "hotel" || s.category === "airbnb") || null);
      }).catch(() => {});
      const favsP = api.get("/favorites").then(({ data }) => data || []).catch(() => []);
      favsP.then(async (favs) => {
        const biz = favs.filter((f) => f.target_type === "business").slice(0, 20);
        const rows = await Promise.all(biz.map((f) =>
          api.get(`/businesses/${f.target_id}`).then(({ data }) => data).catch(() => null),
        ));
        if (!cancelled) setSavedBiz(rows.filter(Boolean));
      });
      api.get("/custom-locations").then(({ data }) => {
        if (cancelled) return;
        setCustomLocs((data || []).filter((d) => d.status !== "deleted"));
      }).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounce Mapbox forward-geocoding so we don't hit the API on every keystroke.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = searchQuery.trim();
    if (q.length < 2) { setGeocodeResults([]); return; }
    searchDebounceRef.current = setTimeout(async () => {
      const rows = await forwardGeocodeHavana(q);
      setGeocodeResults(rows);
    }, 280);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery]);

  // Combined search results — hotel first, then saved businesses, custom
  // locations, then Mapbox geocoding. Local matches always win over remote.
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const rows = [];
    if (hotel && (hotel.lat != null) && (hotel.lng != null)) {
      const name = (hotel.name || "").toLowerCase();
      const addr = (hotel.address || "").toLowerCase();
      const hotelKeyword = lang === "es" ? "hotel" : "hotel";
      if (name.includes(q) || addr.includes(q) || hotelKeyword.includes(q) || "my hotel".includes(q) || "mi hotel".includes(q)) {
        rows.push({
          kind: "hotel", id: "hotel",
          name: hotel.name || t("car_map_search_hotel"),
          address: hotel.address || t("car_map_search_hotel"),
          lat: hotel.lat, lng: hotel.lng,
        });
      }
    }
    savedBiz.forEach((b) => {
      if (b.lat == null || b.lng == null) return;
      const name = (b.name || "").toLowerCase();
      const addr = (b.address || "").toLowerCase();
      const cat = (b.category || "").toLowerCase();
      if (name.includes(q) || addr.includes(q) || cat.includes(q)) {
        rows.push({ kind: "business", id: b.id, name: b.name, address: b.address, lat: b.lat, lng: b.lng });
      }
    });
    customLocs.forEach((c) => {
      if (c.lat == null || c.lng == null) return;
      const name = (c.name || "").toLowerCase();
      const addr = (c.address || "").toLowerCase();
      if (name.includes(q) || addr.includes(q)) {
        rows.push({ kind: "custom", id: c.id, name: c.name, address: c.address, lat: c.lat, lng: c.lng });
      }
    });
    // Remote results — only those not already covered locally (by name + coords).
    const localKey = new Set(rows.map((r) => `${r.name}|${r.lat?.toFixed?.(3)}|${r.lng?.toFixed?.(3)}`));
    geocodeResults.forEach((g) => {
      const k = `${g.name}|${g.lat?.toFixed?.(3)}|${g.lng?.toFixed?.(3)}`;
      if (!localKey.has(k)) rows.push(g);
    });
    return rows.slice(0, 8);
  }, [searchQuery, hotel, savedBiz, customLocs, geocodeResults, lang, t]);

  const handleSearchSelect = (row) => {
    if (!mapRef.current || row.lat == null || row.lng == null) return;
    // Optimistically seed the pin so the popup + bottom panel show the selected
    // name immediately. The settled moveend will then re-reverse-geocode the
    // exact center (often the same name) and refine `address` if needed.
    setSearchQuery("");
    setSearchFocus(false);
    setGeocodeResults([]);
    setPin({ name: row.name, address: row.address || "", lat: row.lat, lng: row.lng });
    mapRef.current.flyTo({ center: [row.lng, row.lat], zoom: 16, speed: 1.6 });
  };

  // Init Mapbox
  useEffect(() => {
    if (!TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = TOKEN;
    const startLng = hasInitialCenter ? initialLng : HAVANA.lng;
    const startLat = hasInitialCenter ? initialLat : HAVANA.lat;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [startLng, startLat],
      zoom: hasInitialCenter ? 15 : 13,
      attributionControl: false,
    });
    mapRef.current = map;

    const runGeocode = async (lat, lng) => {
      setGeocoding(true);
      const result = await reverseGeocode(lng, lat);
      setGeocoding(false);
      if (result) {
        setPin({ name: result.name, address: result.address, lat, lng });
      } else {
        setPin({
          name: coordLabel(lng, lat),
          address: lang === "es" ? "Coordenadas — sin conexión" : "Coordinates — offline",
          lat,
          lng,
        });
      }
    };

    const handleMoveStart = () => {
      setMoving(true);
      // Close the search dropdown when the user starts dragging the map.
      setSearchFocus(false);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
    const handleMoveEnd = () => {
      const c = map.getCenter();
      // Debounce slightly to avoid a flurry of geocoding requests while inertia settles.
      settleTimerRef.current = setTimeout(() => {
        setMoving(false);
        runGeocode(c.lat, c.lng);
      }, 280);
    };
    map.on("movestart", handleMoveStart);
    map.on("moveend", handleMoveEnd);

    const resize = () => map.resize();
    map.on("load", () => {
      resize();
      // Initial reverse-geocode so the user sees a name straight away.
      const c = map.getCenter();
      runGeocode(c.lat, c.lng);
    });
    window.addEventListener("resize", resize);
    const t1 = setTimeout(resize, 150);
    const t2 = setTimeout(resize, 600);

    return () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("resize", resize);
      map.off("movestart", handleMoveStart);
      map.off("moveend", handleMoveEnd);
      // iter212 — cleanup driver markers
      driverMarkersRef.current.forEach((m) => m.remove());
      driverMarkersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [lang]);

  // iter212 · Phase E — Poll nearby drivers every 30s + render dots.
  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    const fetchNearby = async () => {
      try {
        const center = mapRef.current?.getCenter?.();
        const lat = coords?.lat || center?.lat || HAVANA.lat;
        const lng = coords?.lng || center?.lng || HAVANA.lng;
        const { data } = await api.get(
          `/drivers/nearby?lat=${lat}&lng=${lng}&radius_km=3`
        );
        if (!cancelled) setNearbyDrivers(data.drivers || []);
      } catch { /* offline-tolerant */ }
    };

    fetchNearby();
    const id = setInterval(fetchNearby, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [coords]);

  // iter212 · Phase E — Render dots as Mapbox markers whenever the list changes.
  useEffect(() => {
    if (!mapRef.current) return;
    driverMarkersRef.current.forEach((m) => m.remove());
    driverMarkersRef.current = [];
    nearbyDrivers.forEach((d) => {
      const el = document.createElement("button");
      el.setAttribute("data-testid", `driver-marker-${d.id}`);
      el.setAttribute("aria-label", `Driver ${d.full_name || ""}`);
      el.style.cssText = `width:16px;height:16px;border-radius:50%;background:#E8B84B;border:2px solid #0B0B0B;box-shadow:0 0 0 3px rgba(232,184,75,0.30);cursor:pointer;padding:0;`;
      el.addEventListener("click", (ev) => {
        ev.stopPropagation();
        // Tap → open the vendor's public page so the traveller can request
        // them directly (or use their WhatsApp).
        navigate(`/vendors/${d.id}`);
      });
      const marker = new mapboxgl.Marker(el)
        .setLngLat([d.location_lng, d.location_lat])
        .addTo(mapRef.current);
      driverMarkersRef.current.push(marker);
    });
  }, [nearbyDrivers, navigate]);


  const handleRecenter = async () => {
    if (!mapRef.current) return;
    const fly = (lat, lng) => mapRef.current.flyTo({ center: [lng, lat], zoom: 15, speed: 1.4 });
    if (coords) { fly(coords.lat, coords.lng); return; }
    const res = await requestGps("car-map");
    if (res.ok) fly(res.lat, res.lng);
    else if (res.reason !== "soft-denied") {
      toast.message(lang === "es" ? "Ubicación no disponible" : "Location not available");
    }
  };

  const handleConfirm = () => {
    if (!pin || moving) return;
    pushHistory(pin);
    // iter153 — Pickup mode: user is picking their pickup, NOT the
    // destination. Store the chosen pickup and re-fire sendRideRequest
    // with the ORIGINAL destination (stashed in sessionStorage before
    // the user tapped "Change →").
    if (pickupMode) {
      const nextPickup = { lat: pin.lat, lng: pin.lng, address: pin.name || pin.address };
      writeLastPickup(nextPickup);
      const pending = readPendingRide();
      clearPendingRide();
      if (pending?.destination) {
        sendRideRequest(pending.destination, {
          source: pending.source || "car-map-pickup",
          pickup: nextPickup,
        });
      } else {
        // No pending destination — just persist and go back.
        navigate(-1);
      }
      return;
    }
    // iter212 · Phase D — Try to open a bidding job first. If the current
    // transport subcategory has `bidding_enabled=false` the backend returns
    // `{ok:true, bidding:false}` and we transparently fall through to the
    // legacy WhatsApp flow. Anonymous users are supported via `user_session`.
    const trySubcat = (() => {
      try { return localStorage.getItem("habano.transport_subcategory") || "bike"; }
      catch { return "bike"; }
    })();
    const pickupCoords = fromAirport ? AIRPORT : (coords || null);
    const clientId = (() => {
      try { return localStorage.getItem("habano.client_id"); } catch { return null; }
    })();
    api.post("/jobs", {
      type: "ride",
      subcategory: trySubcat,
      pickup_lat: pickupCoords?.lat || pin.lat,
      pickup_lng: pickupCoords?.lng || pin.lng,
      pickup_address: pickupCoords?.address || pickupCoords?.name || null,
      destination_lat: pin.lat,
      destination_lng: pin.lng,
      destination_address: pin.name || pin.address || null,
      destination_neighborhood: pin.address ? pin.address.split(",")[0] : null,
      user_session: clientId || undefined,
    }).then(({ data }) => {
      if (data && data.bidding && data.job_id) {
        navigate(`/ride/bids/${data.job_id}`);
        return;
      }
      // Bidding not enabled — legacy WhatsApp handoff.
      sendRideRequest(pin, {
        source: fromAirport ? "car-map-airport" : "car-map",
        pickup: fromAirport ? AIRPORT : null,
      });
    }).catch(() => {
      // Any failure → fall back to WhatsApp so travellers never get stuck.
      sendRideRequest(pin, {
        source: fromAirport ? "car-map-airport" : "car-map",
        pickup: fromAirport ? AIRPORT : null,
      });
    });
  };

  const ctaActive = !!pin && !moving && !geocoding;

  return (
    <main data-testid="car-map-page" className="fixed inset-0 overflow-hidden" style={{ background: "#0B0B0B" }}>
      {/* Map canvas */}
      <div className="absolute inset-0" data-testid="car-map-stage">
        {TOKEN ? (
          <div ref={containerRef} className="absolute inset-0" style={{ width: "100%", height: "100%" }} />
        ) : (
          <div className="absolute inset-0 grid place-items-center" style={{ background: "#0d0d0d" }}>
            <div
              data-testid="car-map-no-token"
              className="text-center px-6"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}
            >
              {t("car_map_no_token")}
            </div>
          </div>
        )}
      </div>

      {/* Top gradient overlay */}
      <div
        className="absolute left-0 right-0 top-0"
        style={{
          background: "linear-gradient(180deg, rgba(11,11,11,0.95) 0%, rgba(11,11,11,0.7) 60%, rgba(11,11,11,0) 100%)",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)",
          paddingBottom: 28,
          paddingLeft: 14,
          paddingRight: 14,
          zIndex: 12,
        }}
      >
        <div className="flex items-start gap-3">
          <button
            type="button"
            data-testid="car-map-back"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="grid place-items-center rounded-full shrink-0"
            style={{
              minHeight: 44,
              minWidth: 44,
              background: "rgba(11,11,11,0.85)",
              border: "0.5px solid rgba(232,184,75,0.3)",
            }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
          </button>
          <div className="flex-1 min-w-0 pt-0.5">
            <h1
              data-testid="car-map-title"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", lineHeight: 1.15 }}
            >
              {fromAirport ? t("car_map_from_airport_title") : t("car_map_title")}
            </h1>
            <div
              className="mt-1"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}
            >
              {fromAirport ? t("car_map_from_airport_sub") : t("car_map_sub")}
            </div>
            {fromAirport && (
              <div
                data-testid="car-map-pickup-badge"
                className="mt-2 inline-flex items-center gap-1.5"
                style={{
                  background: "rgba(232,184,75,0.12)",
                  border: "1px solid rgba(232,184,75,0.4)",
                  borderRadius: 999,
                  padding: "4px 10px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 11,
                  color: "#E8B84B",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                <Plane className="w-3 h-3" strokeWidth={2} /> {lang === "es" ? "Recogida: Aeropuerto" : "Pickup: Airport"}
              </div>
            )}
          </div>
        </div>

        {/* Search bar — slot between back/title and the map. */}
        <div className="mt-3 relative">
          <label
            className="flex items-center gap-2"
            style={{
              height: 40,
              padding: "0 12px",
              background: "rgba(20,20,20,0.95)",
              border: "1px solid rgba(232,184,75,0.35)",
              borderRadius: 12,
            }}
          >
            <Search className="w-4 h-4 shrink-0" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
            <input
              data-testid="car-map-search-input"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchFocus(true); }}
              onFocus={() => setSearchFocus(true)}
              placeholder={t("car_map_search_ph")}
              className="flex-1 min-w-0 bg-transparent outline-none"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#F5F1E8",
                border: "none",
              }}
              autoComplete="off"
              spellCheck={false}
            />
            {searchQuery && (
              <button
                type="button"
                data-testid="car-map-search-clear"
                aria-label={t("car_map_search_clear")}
                onClick={() => { setSearchQuery(""); setGeocodeResults([]); setSearchFocus(false); }}
                className="grid place-items-center shrink-0"
                style={{ width: 24, height: 24, color: "#B0AAA3", background: "transparent", border: "none" }}
              >
                <XIcon className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </label>

          {/* Results dropdown — closes when user starts dragging the map (handleMoveStart). */}
          {searchFocus && searchQuery.trim().length > 0 && (
            <ul
              data-testid="car-map-search-results"
              className="absolute left-0 right-0 mt-1.5"
              style={{
                background: "rgba(20,20,20,0.98)",
                border: "0.5px solid rgba(232,184,75,0.2)",
                borderRadius: 10,
                listStyle: "none",
                margin: 0,
                padding: 4,
                zIndex: 30,
                maxHeight: 320,
                overflowY: "auto",
              }}
            >
              {searchResults.length === 0 ? (
                <li
                  data-testid="car-map-search-empty"
                  style={{
                    padding: "10px 12px",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    color: "#B0AAA3",
                  }}
                >
                  {t("car_map_search_no_results")}
                </li>
              ) : (
                searchResults.map((row, idx) => (
                  <SearchResultRow
                    key={`${row.kind}-${row.id || idx}-${row.lat}-${row.lng}`}
                    row={row}
                    onSelect={() => handleSearchSelect(row)}
                    testid={`car-map-search-result-${idx}`}
                  />
                ))
              )}
            </ul>
          )}
        </div>
      </div>

      {/* Center crosshair */}
      <div
        data-testid="car-map-crosshair"
        className="pointer-events-none absolute"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 60,
          height: 60,
          zIndex: 14,
        }}
      >
        {/* Cross lines */}
        <span style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "rgba(232,184,75,0.55)", transform: "translateY(-0.5px)" }} />
        <span style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "rgba(232,184,75,0.55)", transform: "translateX(-0.5px)" }} />
        {/* Outer pulsing ring */}
        <span
          className={moving ? "habano-cross-pulse" : ""}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: moving ? "1.5px solid rgba(232,184,75,0.55)" : "1.5px solid rgba(232,184,75,0.9)",
            background: moving ? "rgba(232,184,75,0.04)" : "rgba(232,184,75,0.10)",
            boxShadow: moving ? "0 0 14px rgba(232,184,75,0.35)" : "0 0 24px rgba(232,184,75,0.55)",
          }}
        />
        {/* Inner solid dot */}
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "#E8B84B",
            boxShadow: "0 2px 8px rgba(0,0,0,0.6)",
          }}
        />
        {/* Drop shadow under the crosshair to suggest a pin */}
        <span
          style={{
            position: "absolute",
            top: "calc(50% + 12px)",
            left: "50%",
            transform: "translate(-50%, 0)",
            width: 14,
            height: 4,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.45)",
            filter: "blur(2px)",
          }}
        />
      </div>

      {/* Recenter FAB */}
      <button
        type="button"
        data-testid="car-map-recenter"
        onClick={handleRecenter}
        aria-label={lang === "es" ? "Centrar en mí" : "Recenter on me"}
        className="absolute"
        style={{
          right: 16,
          top: "50%",
          transform: "translateY(-50%)",
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "#E8B84B",
          color: "#0B0B0B",
          border: "none",
          display: "grid",
          placeItems: "center",
          boxShadow: "0 0 0 3px rgba(232,184,75,0.3), 0 6px 16px -4px rgba(0,0,0,0.6)",
          zIndex: 13,
        }}
      >
        <LocateFixed className="w-5 h-5" strokeWidth={2} />
      </button>

      {/* Location preview popup — sits above the crosshair when not moving */}
      {pin && !moving && (
        <div
          data-testid="car-map-popup"
          className="absolute pointer-events-none"
          style={{
            top: "calc(50% - 78px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#171717",
            border: "1px solid rgba(232,184,75,0.45)",
            borderRadius: 10,
            padding: "8px 12px",
            maxWidth: 260,
            zIndex: 13,
            boxShadow: "0 8px 24px -8px rgba(0,0,0,0.7)",
          }}
        >
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: "#F5F1E8",
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {pin.name}
          </div>
          {pin.address && (
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11,
                color: "#B0AAA3",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginTop: 2,
              }}
            >
              {pin.address}
            </div>
          )}
          {/* Pointer tail */}
          <span
            style={{
              position: "absolute",
              bottom: -7,
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: 12,
              height: 12,
              background: "#171717",
              borderRight: "1px solid rgba(232,184,75,0.45)",
              borderBottom: "1px solid rgba(232,184,75,0.45)",
            }}
          />
        </div>
      )}

      {/* Bottom panel */}
      <div
        className="absolute left-0 right-0 bottom-0"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          paddingTop: 16,
          paddingLeft: 18,
          paddingRight: 18,
          background: "linear-gradient(180deg, rgba(11,11,11,0) 0%, rgba(11,11,11,0.92) 35%, #0B0B0B 100%)",
          zIndex: 15,
        }}
      >
        {moving || !pin ? (
          <div
            data-testid="car-map-panel-drag"
            className="text-center"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#E8B84B", letterSpacing: "0.04em", paddingBottom: 14 }}
          >
            {geocoding ? t("car_map_loading") : t("car_map_panel_drag")}
          </div>
        ) : (
          <div
            data-testid="car-map-panel-confirm"
            className="mb-3"
            style={{
              background: "#171717",
              border: "1px solid rgba(232,184,75,0.35)",
              borderRadius: 14,
              padding: "12px 14px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span
              className="grid place-items-center rounded-full shrink-0"
              style={{ width: 36, height: 36, background: "rgba(232,184,75,0.15)" }}
            >
              <MapPin className="w-4 h-4" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div
                data-testid="car-map-pin-name"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 15,
                  color: "#F5F1E8",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {pin.name}
              </div>
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 11,
                  color: "#B0AAA3",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {pin.address || coordLabel(pin.lng, pin.lat)}
              </div>
            </div>
            <button
              type="button"
              data-testid="car-map-change"
              onClick={() => {
                // Nudge user to drag again — clear pin so the panel reverts to State 1.
                setMoving(true);
                setTimeout(() => setMoving(false), 50);
              }}
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#E8B84B", letterSpacing: "0.1em", textTransform: "uppercase", background: "transparent", border: "none" }}
            >
              {t("car_map_change")}
            </button>
          </div>
        )}
        <button
          type="button"
          data-testid="car-map-confirm-btn"
          onClick={handleConfirm}
          disabled={!ctaActive}
          aria-disabled={!ctaActive}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{
            minHeight: 56,
            background: ctaActive ? "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)" : "#1a1a1a",
            color: ctaActive ? "#0B0B0B" : "#B0AAA3",
            borderRadius: 14,
            border: ctaActive ? "none" : "1px solid rgba(232,184,75,0.2)",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            opacity: ctaActive ? 1 : 0.65,
            cursor: ctaActive ? "pointer" : "not-allowed",
            transition: "all 0.2s ease",
          }}
        >
          <CarIcon className="w-4 h-4" strokeWidth={2} /> {pickupMode ? t("ride_pickup_set_here") : t("car_map_panel_cta")}
        </button>
        <p className="text-center mt-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3" }}>
          {t("car_map_panel_hint")}
        </p>
      </div>

      <style>{`
        @keyframes habanoCrossPulse {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50%      { transform: scale(1.18); opacity: 0.6; }
        }
        .habano-cross-pulse { animation: habanoCrossPulse 1s ease-in-out infinite; }
      `}</style>

      {/* No bottom nav — full-screen experience. */}
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// SearchResultRow — one row inside the search-results dropdown. Icon left,
// name + sub right, chevron-style indication on the right via gold accent.
// ──────────────────────────────────────────────────────────────────────────
function SearchResultRow({ row, onSelect, testid }) {
  const ICON_MAP = {
    hotel: Bed,
    business: Heart,
    custom: Gem,
    geocode: Globe,
  };
  const Icon = ICON_MAP[row.kind] || MapPin;
  const isHotel = row.kind === "hotel";
  return (
    <li>
      <button
        type="button"
        data-testid={testid}
        onClick={onSelect}
        className="w-full flex items-center gap-2.5 transition-colors hover:bg-white/[0.03]"
        style={{
          minHeight: 48,
          padding: "8px 10px",
          background: "transparent",
          border: "none",
          textAlign: "left",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        <span
          className="grid place-items-center shrink-0"
          style={{
            width: 26,
            height: 26,
            borderRadius: 999,
            background: isHotel ? "rgba(232,184,75,0.18)" : "rgba(232,184,75,0.08)",
            color: "#E8B84B",
            border: "0.5px solid rgba(232,184,75,0.3)",
          }}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
        </span>
        <span className="flex-1 min-w-0">
          <span
            style={{
              display: "block",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11,
              fontWeight: 500,
              color: "#F5F1E8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.name}
          </span>
          {row.address ? (
            <span
              style={{
                display: "block",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 10,
                color: "#B0AAA3",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.address}
            </span>
          ) : null}
        </span>
        <span style={{ color: "#B0AAA3", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>›</span>
      </button>
    </li>
  );
}
